import React, { Fragment, useMemo } from "react";
import { Btn } from "../../../AbstractElements";
import { Card, CardBody, Badge, UncontrolledTooltip } from "reactstrap";
import DataTable from "../../Components/DataTable";
import { createColumnHelper } from "@tanstack/react-table";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";
import { API_BASE_URL } from "../../api/api";

export const CountriesAvailableList = ({ data, isLoading, onToggleStatus, onEditCountry }) => {
  const columnHelper = createColumnHelper();

  const columns = useMemo(() => [
    columnHelper.accessor("name", { header: "Nombre del país" }),

    columnHelper.display({
      id: "paymentGateways",
      header: "Pasarelas de pago",
      cell: (props) => {
        const gateways = props.row.original.paymentGateways || [];
        return gateways.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {gateways.map((gw, idx) => (
              <Badge key={idx} color="light" pill>{gw}</Badge>
            ))}
          </div>
        ) : (
          <span>Sin pasarelas</span>
        );
      },
    }),

    columnHelper.accessor("currency", {
      header: "Moneda local"
    }),

    columnHelper.accessor("pointValue", {
      header: "Valor Puntos",
      cell: (info) => `$ ${info.getValue()?.toLocaleString() ?? 0}`,
    }),

    columnHelper.display({
      id: "status",
      header: "Estado",
      cell: (props) => {
        const status = props.row.original.status;
        const color = status ? "success" : "danger";
        return <Badge color={color}>{status ? "Activo" : "Inactivo"}</Badge>;
      },
    }),
    columnHelper.display({
      id: "files",
      header: "Archivos",
      cell: (props) => {
        const country = props.row.original;

        const files = [
          { key: "sanitaryRegistration", label: "Registro Sanitario" },
          { key: "personalDataAuthorization", label: "Autorización Datos" },
          { key: "termsAndConditions", label: "Términos y Condiciones" },
        ];

        return (
          <div className="d-flex flex-column gap-1">
            {files.map(({ key, label }) =>
              country[key] ? (
                <a
                  key={key}
                  href={`${API_BASE_URL}/product/countries-available/get-file/${country[key]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center"
                  download
                  title={`Descargar ${label}`}
                >
                  <i className="fa fa-download me-1"></i>
                  <small>{label}</small>
                </a>
              ) : (
                <small key={key} className="text-muted">
                  Sin {label}
                </small>
              )
            )}
          </div>
        );
      },
    }),


    columnHelper.accessor("created_at", {
      header: "Fecha creación",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),

    columnHelper.display({
      id: "actions",
      header: "Acciones",
      cell: (props) => {
        const country = props.row.original;
        const isActive = country.status;
        const toggleColor = isActive ? "danger" : "success";
        const toggleIcon = isActive ? "fa fa-toggle-off" : "fa fa-toggle-on";
        const toggleText = isActive ? "Inactivar" : "Activar";

        const editId = `edit-btn-${country.id}`;
        const toggleId = `toggle-btn-${country.id}`;

        const handleEdit = () => {
          onEditCountry(country);
        };

        const handleToggle = () => {
          Swal.fire({
            title: `¿Quieres ${toggleText} este país?`,
            text: `País: ${country.name}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: isActive ? "#d33" : "#3085d6",
            cancelButtonColor: "#6c757d",
            confirmButtonText: `Sí, ${toggleText}`,
            cancelButtonText: "Cancelar",
          }).then((result) => {
            if (result.isConfirmed) {
              onToggleStatus(country.id);
            }
          });
        };

        return (
          <div className="d-flex gap-2">
            <Btn
              attrBtn={{
                color: "primary",
                size: "sm",
                type: "button",
                id: editId,
                onClick: handleEdit,
              }}
            >
              <i className="fa fa-edit"></i>
            </Btn>
            <UncontrolledTooltip placement="top" target={editId}>
              Editar
            </UncontrolledTooltip>

            <Btn
              attrBtn={{
                color: toggleColor,
                size: "sm",
                type: "button",
                id: toggleId,
                onClick: handleToggle,
              }}
            >
              <i className={toggleIcon}></i>
            </Btn>
            <UncontrolledTooltip placement="top" target={toggleId}>
              {toggleText}
            </UncontrolledTooltip>
          </div>
        );
      },
    }),
  ], [onToggleStatus, onEditCountry]);

  return (
    <Fragment>
      <Card>
        <CardBody>
          <DataTable
            data={data ?? []}
            noHeader
            pagination
            paginationServer
            columns={columns}
            highlightOnHover
            striped
            responsive
            progressPending={isLoading}
          />
        </CardBody>
      </Card>
    </Fragment>
  );
};
